import { useState, useEffect } from "react";
import useTitanApp from "../../../hooks/useTitanApp";
import {
  getPatientByPatientId,
  getPatientVitals,
  GetPatientVitalsSummaryByPatientId,
  setCurrentOpenDeleteVitalsModal,
  updatePatientVitals,
} from "../../../dispatchers/patientDispatchers";
import { closeVitalsModal } from "../../../features/patientSlice/patientSlice";
import { Modal } from "react-bootstrap";
import { IoClose } from "react-icons/io5";
import { CiWavePulse1 } from "react-icons/ci";
import { LuHeart } from "react-icons/lu";
import { BsThermometer } from "react-icons/bs";
import { LiaWeightHangingSolid } from "react-icons/lia";
import { Ruler, Circle, Calculator, Wind } from "lucide-react";
import "../../../assets/css/PatientDeleteVitalsModal.scss";

export default function PatientDeleteVitalsModal() {
  const { appState, patientState } = useTitanApp();
  const { darkMode } = appState || {};
  const {
    selectedPatient,
    selectedPatientDetails,
    showDeleteVitalsModal,
    deletePatientVitalsLoader,
    vitalType,
    vitalToDelete,
    consultationId,
  } = patientState || {};
  const [selectedVital, setSelectedVital] = useState(null);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);

  const closeModal = () => {
    setCurrentOpenDeleteVitalsModal(false);
    setSelectedVital(null);
    closeVitalsModal();
  };

  const vitals = [
    {
      icon: <LuHeart color={darkMode ? "#bdbdbd" : "#0a0a0a"} />,
      label: "Heart Rate",
      desc: "Beats per minute (BPM)",
    },
    {
      icon: (
        <LiaWeightHangingSolid
          size={19}
          color={darkMode ? "#bdbdbd" : "#0a0a0a"}
        />
      ),
      label: "Weight",
      desc: "Body weight measurement",
    },
    {
      icon: <CiWavePulse1 size={20} color={darkMode ? "#bdbdbd" : "#0a0a0a"} />,
      label: "Blood Pressure",
      desc: "Systolic/Diastolic pressure",
    },
    {
      icon: (
        <BsThermometer size={18} color={darkMode ? "#bdbdbd" : "#0a0a0a"} />
      ),
      label: "Temperature",
      desc: "Body temperature",
    },
    {
      icon: <Wind size={18} color={darkMode ? "#bdbdbd" : "#0a0a0a"} />,
      label: "Respiratory Rate",
      desc: "Body temperature",
    },
    {
      icon: <Ruler size={18} color={darkMode ? "#bdbdbd" : "#0a0a0a"} />,
      label: "Height",
      desc: "Body temperature",
    },
    {
      icon: <Circle size={18} color={darkMode ? "#bdbdbd" : "#0a0a0a"} />,
      label: "Circumference",
      desc: "Body temperature",
    },
    {
      icon: <Calculator size={18} color={darkMode ? "#bdbdbd" : "#0a0a0a"} />,
      label: "BMI Calculator",
      desc: "Body temperature",
    },
  ];

  const handleDeleteClick = async (values) => {
    setButtonsDisabled(true);
    if (!selectedVital) return;

    const obj = {
      consultationId,
      patientId: String(selectedPatientDetails?.patientID || ""),
      patientName: String(selectedPatient || ""),
    };

    if (selectedVital.label === "Heart Rate") {
      obj.heartRate = {
        Value: String(values.value || ""),
        Unit: "BPM",
      };
    } else if (selectedVital.label === "Weight") {
      obj.weight = {
        Value: String(values.value || ""),
        Unit: String(values.unit || ""),
      };
    } else if (selectedVital.label === "Blood Pressure") {
      obj.bloodPressure = {
        Systolic: { Value: String(values.systolic || ""), Unit: "mmHg" },
        Diastolic: { Value: String(values.siastolic || ""), Unit: "mmHg" },
      };
    } else if (selectedVital.label === "Temperature") {
      obj.temperature = {
        Value: String(values.value || ""),
        Unit: String(values.unit || ""),
      };
    } else if (selectedVital.label === "Respiratory Rate") {
      obj.respiratoryRate = {
        Value: String(values.value || ""),
        Unit: "breaths/min",
      };
    } else if (selectedVital.label === "Height") {
      obj.height = {
        Value: String(values.value || ""),
        Unit: String(values.unit || ""),
      };
    } else if (selectedVital.label === "Circumference") {
      obj.circumferenceValue = {
        Unit: String(values.unit || ""),
        Waist: String(values.waistCircumference || ""),
        Hip: String(values.hipCircumference || ""),
        Neck: String(values.neckCircumference || ""),
      };
      obj.circumference = {
        Unit: String(values.Unit || ""),
        Value: `Waist: ${String(values.waistCircumference || "")} Hip: ${String(
          values.hipCircumference || "",
        )} Neck: ${String(values.neckCircumference || "")}`,
      };
    } else if (selectedVital.label === "BMI Calculator") {
      const heightInMeters =
        values.HeightUnit === "cm"
          ? parseFloat(values.height) / 100
          : parseFloat(values.height) * 0.3048; // ft to meters

      const weightInKg =
        values.WeightUnit === "kg"
          ? parseFloat(values.weight)
          : parseFloat(values.weight) * 0.453592; // lbs to kg

      const bmi = weightInKg / (heightInMeters * heightInMeters);
      obj.height = {
        Value: String(values.height || ""),
        Unit: String(values.heightUnit || ""),
      };
      obj.weight = {
        Value: String(values.weight || ""),
        Unit: String(values.weightUnit || ""),
      };
      obj.bmi = {
        Value: String(bmi.toFixed(2)),
        Unit: "kg/m²",
      };
    }

    try {
      await updatePatientVitals({
        isDelete: true,
        vitalsModal: obj,
      });

      await getPatientVitals(selectedPatientDetails?.patientID);
      await GetPatientVitalsSummaryByPatientId(
        selectedPatientDetails?.patientID,
      );
      closeModal();
    } catch (error) {
      console.error("Error in deleting patient vitals:", error);
    }
  };

  useEffect(() => {
    if (vitalType) {
      let mappedType = vitalType;
      if (vitalType === "BMI" || vitalType === "Bmi")
        mappedType = "BMI Calculator";
      if (vitalType === "HeartRate") mappedType = "Heart Rate";
      if (vitalType === "RespiratoryRate") mappedType = "Respiratory Rate";
      const foundVital = vitals.find((v) => v.label === mappedType);
      setSelectedVital(foundVital || null);
    }
  }, [vitalType]);
  return (
    <Modal
      show={showDeleteVitalsModal}
      onHide={closeModal}
      centered
      backdrop="static"
      dialogClassName="pc-modal-delete-vitals delete-vitals"
      size="md"
    >
      <div className="header">
        <div className="info">
          <p className="label">
            {selectedVital ? (
              <>
                <div>{selectedVital?.icon} &nbsp;</div>
                Delete {selectedVital?.label}
              </>
            ) : (
              "Delete"
            )}
          </p>
        </div>
        <IoClose
          size={20}
          color={darkMode ? "#bdbdbd" : "#7c7c7c"}
          onClick={closeModal}
        />
      </div>
      <div className="body">
        <div>
          <p>
            Are you sure you want to delete the vital{" "}
            {selectedVital ? `"${selectedVital.label}"` : "this"} ? Do you want
            to continue?
          </p>
        </div>
        <div className="pc-delete-vitals-footer-actions">
          <button className="cancel" type="button" onClick={closeModal}>
            Cancel
          </button>
          <button
            className="done"
            type="submit"
            disabled={buttonsDisabled}
            onClick={() => handleDeleteClick(vitalToDelete)}
          >
            {deletePatientVitalsLoader === true ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
